import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { AdminGate } from "../../components/admin/AdminGate";
import { Tabs, type TabDefinition } from "../../components/admin/Tabs";
import { AuditTrailContent } from "./AuditTrailPage";
import { DormancyThresholdSection } from "./DormancyThresholdSection";
import { CurrencyRatesContent } from "./CurrencyRatesPage";
import { McpTokenLifetimesSection } from "./McpTokenLifetimesSection";
import { McpCommandRetentionSection } from "./McpCommandRetentionSection";
import { ModelNamesContent } from "./ModelNamesPage";
import { RequestTypesContent } from "./RequestTypesPage";
import { CapexBudgetSection } from "./CapexBudgetSection";
import { SessionLimitsSection } from "./SessionLimitsSection";
import { TrustedProxiesSection } from "./TrustedProxiesSection";
import { ConfigurationContent } from "./ConfigurationContent";
import { AccountsContent } from "./AccountsContent";
import { FeaturesContent } from "./FeaturesContent";
import { useSession } from "../../session/SessionContext";
import { SettingGrid, SettingGroup } from "../../components/admin/SettingPanel";

/** The tabs of System Settings, in the order they are shown. Each tab's id is its address: `/admin/system/<id>`. */
const systemTabIds = ["audit-trail", "accounts", "currency-rates", "model-names", "request-types", "app-settings", "features", "configuration"] as const;
type SystemTabId = (typeof systemTabIds)[number];
const defaultTabId = systemTabIds[0];

/** A System Settings tab, with the permissions that open it. A tab is shown only to a person who holds at least one of them. */
type SystemTab = TabDefinition & { id: SystemTabId; permissions: readonly string[] };

/**
 * The "System Settings" page consolidates system monitoring and configuration screens
 * (Audit Trail, Accounts, Currency Rates, Model Names, Request Types, App Settings, Features and Configuration) into a single tabbed
 * interface to reduce admin navigation clutter. Each tab has its own address, so it can be opened directly.
 * A person sees only the tabs they hold a permission for; an address for a tab they cannot open goes to the first tab they can.
 */
export function SystemSettingsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();
  const { snapshot } = useSession();

  const held = snapshot.status === "signed-in" ? snapshot.permissions : [];
  const tabs: SystemTab[] = [
    { id: "audit-trail", permissions: ["audit.read"], label: t("admin.system.tabs.auditTrail"), content: <AuditTrailContent /> },
    { id: "accounts", permissions: ["accounts.administer"], label: t("admin.system.tabs.accounts"), content: <AccountsContent /> },
    { id: "currency-rates", permissions: ["settings.write"], label: t("admin.system.tabs.currencyRates"), content: <CurrencyRatesContent /> },
    { id: "model-names", permissions: ["settings.write"], label: t("admin.system.tabs.modelNames"), content: <ModelNamesContent /> },
    { id: "request-types", permissions: ["settings.write"], label: t("admin.system.tabs.requestTypes"), content: <RequestTypesContent /> },
    {
      id: "app-settings",
      permissions: ["settings.write"],
      label: t("admin.system.tabs.appSettings"),
      content: (
        <>
          <SettingGrid>
            <CapexBudgetSection />
            <DormancyThresholdSection />
          </SettingGrid>
          <SettingGroup id="sign-in-and-network" title={t("admin.appSettings.signIn.title")} intro={t("admin.appSettings.signIn.intro")}>
            <SessionLimitsSection />
            <TrustedProxiesSection />
          </SettingGroup>
          <SettingGroup id="connected-applications" title={t("admin.appSettings.connectedApplications.title")} intro={t("admin.appSettings.connectedApplications.intro")}>
            <McpTokenLifetimesSection />
            <McpCommandRetentionSection />
          </SettingGroup>
        </>
      ),
    },
    { id: "features", permissions: ["settings.write"], label: t("admin.system.tabs.features"), content: <FeaturesContent /> },
    { id: "configuration", permissions: ["configuration.import-export"], label: t("admin.system.tabs.configuration"), content: <ConfigurationContent /> },
  ];

  const permittedTabs = tabs.filter((definition) => definition.permissions.some((permission) => held.includes(permission)));
  const permittedIds: readonly string[] = permittedTabs.map((definition) => definition.id);
  const isUnknownTab = tab === undefined || !(systemTabIds as readonly string[]).includes(tab);

  // A person with none of the tab permissions has no tab to go to; the gate below shows them the denied state.
  if (isUnknownTab || (permittedTabs.length > 0 && !permittedIds.includes(tab))) {
    return <Navigate to={`/admin/system/${permittedTabs[0]?.id ?? defaultTabId}`} replace />;
  }

  return (
    <div>
      <AdminGate permissions={tabs.flatMap((definition) => definition.permissions)} mode="any" title={t("admin.system.denied.title")} description={t("admin.system.denied.description")}>
        <Tabs tabs={permittedTabs} activeId={tab} onSelect={(id) => void navigate(`/admin/system/${id}`)} />
      </AdminGate>
    </div>
  );
}
